"use client";

import { useEffect, useRef, useState } from "react";
import {
  CAPTCHA_REQUIRED_MESSAGE,
  getCaptchaErrorMessage,
} from "@/lib/turnstile-client";
import type { Locale } from "@/lib/i18n";
import TurnstileWidget from "./TurnstileWidget";
import { isTurnstileWidgetEnabled } from "@/lib/turnstile-widget";

type Props = {
  defaultName?: string;
  defaultContact?: string;
  isAuthenticated?: boolean;
  locale?: Locale;
};

const CATEGORY_OPTIONS: Record<Locale, { value: string; label: string }[]> = {
  ko: [
    { value: "ad", label: "광고 제휴" },
    { value: "partnership", label: "서비스 제휴" },
    { value: "proposal", label: "서비스 제안" },
    { value: "etc", label: "기타" },
  ],
  en: [
    { value: "ad", label: "Advertising partnership" },
    { value: "partnership", label: "Service partnership" },
    { value: "proposal", label: "Product proposal" },
    { value: "etc", label: "Other" },
  ],
};

const MAX_MESSAGE = 2000;

export default function ContactForm({
  defaultName = "",
  defaultContact = "",
  isAuthenticated = false,
  locale = "ko",
}: Props) {
  const [category, setCategory] = useState<string>("ad");
  const [name, setName] = useState(defaultName);
  const [contact, setContact] = useState(defaultContact);
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const turnstileTokenRef = useRef<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const mountedAtRef = useRef<number>(0);

  useEffect(() => {
    mountedAtRef.current = Date.now();
  }, []);

  const remaining = MAX_MESSAGE - message.length;
  const copy =
    locale === "ko"
      ? {
          required: "필수 항목을 모두 입력해 주세요.",
          maxMessage: `문의 내용은 최대 ${MAX_MESSAGE.toLocaleString()}자입니다.`,
          captchaRequired: CAPTCHA_REQUIRED_MESSAGE,
          captchaFailed: "보안 확인에 실패했어요. 다시 시도해 주세요.",
          rateLimited: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
          submitFailed:
            "문의 전송에 실패했어요. 입력 내용을 확인한 뒤 다시 시도해 주세요.",
          networkFailed: "네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.",
          submittedTitle: "문의가 접수되었어요",
          submittedBody:
            "영업일 기준 3~5일 내에 남겨 주신 연락처로 회신드리겠습니다.",
          authNotice:
            "로그인한 계정으로 제출됩니다. 이름·연락처는 수정할 수 있어요.",
          category: "분류",
          name: "성함",
          contact: "연락처",
          contactPlaceholder: "이메일 또는 전화번호",
          company: "회사 · 소속 (선택)",
          message: "문의 내용",
          remaining: "남음",
          privacy:
            "제출된 정보는 제휴 문의 응답 목적으로만 사용되며, 처리 완료 후 30일이 지나면 자동으로 파기됩니다. IP 주소 등 접속 기록은 24시간 후 삭제됩니다.",
          pending: "전송 중...",
          submit: "문의 보내기",
        }
      : {
          required: "Please fill in all required fields.",
          maxMessage: `Message is limited to ${MAX_MESSAGE.toLocaleString("en-US")} characters.`,
          captchaRequired: "Please complete the security check.",
          captchaFailed: "Security verification failed. Please try again.",
          rateLimited: "Too many requests. Please try again later.",
          submitFailed:
            "We could not send your message. Check the fields and try again.",
          networkFailed: "A network error occurred. Please try again later.",
          submittedTitle: "Message received",
          submittedBody:
            "We will reply to the contact details you provided within 3 to 5 business days.",
          authNotice:
            "This will be submitted with your signed-in account. You can edit the name and contact details.",
          category: "Category",
          name: "Name",
          contact: "Contact",
          contactPlaceholder: "Email address or phone number",
          company: "Company or organization (optional)",
          message: "Message",
          remaining: "left",
          privacy:
            "Submitted information is used only to respond to partnership inquiries and is deleted 30 days after processing. Access logs such as IP addresses are deleted after 24 hours.",
          pending: "Sending...",
          submit: "Send message",
        };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim() || !contact.trim() || !message.trim()) {
      setError(copy.required);
      return;
    }
    if (message.length > MAX_MESSAGE) {
      setError(copy.maxMessage);
      return;
    }
    const turnstileToken = turnstileTokenRef.current;
    if (isTurnstileWidgetEnabled() && !turnstileToken) {
      setError(copy.captchaRequired);
      return;
    }
    setError(null);
    setIsPending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          name,
          contact,
          company,
          message,
          website,
          mountedAt: mountedAtRef.current,
          turnstileToken,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      const captchaError = await getCaptchaErrorMessage(res);
      if (captchaError) {
        setError(locale === "ko" ? captchaError : copy.captchaFailed);
        return;
      }
      if (res.status === 429) {
        setError(copy.rateLimited);
        return;
      }
      setError(copy.submitFailed);
    } catch {
      setError(copy.networkFailed);
    } finally {
      setIsPending(false);
      turnstileTokenRef.current = null;
      setTurnstileResetSignal((value) => value + 1);
    }
  }

  if (submitted) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <h2 className="font-display text-xl text-emerald-800">
          {copy.submittedTitle}
        </h2>
        <p className="text-sm leading-6 text-emerald-700">
          {copy.submittedBody}
        </p>
      </section>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mx-auto flex w-full max-w-lg flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop"
      noValidate
    >
      {isAuthenticated && (
        <p className="rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand-800">
          {copy.authNotice}
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.category}
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        >
          {CATEGORY_OPTIONS[locale].map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.name}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          maxLength={50}
          required
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.contact}
        <input
          type="text"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          disabled={isPending}
          maxLength={120}
          required
          placeholder={copy.contactPlaceholder}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.company}
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={isPending}
          maxLength={100}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.message}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isPending}
          rows={6}
          maxLength={MAX_MESSAGE}
          required
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
        <span className="text-right text-xs text-slate-400">
          {message.length.toLocaleString()} / {MAX_MESSAGE.toLocaleString()}
          <span className="ml-1 text-slate-300">
            ({remaining.toLocaleString()} {copy.remaining})
          </span>
        </span>
      </label>

      <p className="text-xs text-slate-500">
        {copy.privacy}
      </p>

      <div aria-hidden className="absolute -left-[9999px]">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      <TurnstileWidget
        action="contact"
        onTokenChange={(token) => {
          turnstileTokenRef.current = token;
        }}
        resetSignal={turnstileResetSignal}
      />

      <button
        type="submit"
        disabled={isPending}
        className="rounded-xl bg-brand-gradient py-2 text-sm font-medium text-white shadow-pop disabled:opacity-60"
      >
        {isPending ? copy.pending : copy.submit}
      </button>
    </form>
  );
}
